import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { customAPI } from "services/utils/CustomAPI";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import RequestStatusTimeline from "components/DesignSystem/RequestStatusTimeline";
import ApprovalEditAuditTrail from "components/DesignSystem/ApprovalEditAuditTrail";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import {
  detailAccentPalette,
  detailCardSx,
  detailSectionLabelSx,
  RequestDetailField,
} from "components/DesignSystem/RequestDetailField";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import {
  designColors,
  designShell,
} from "services/constants/designTokens";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { approvePanelUi, useGoToApprovalCenter } from "../approveUi";
import {
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { canonicalPermissionStatus, permissionStatusVisual } from "./PermissionStatusCards";
import { Apply as PermissionApplyPanel } from "./Apply";

const toISODate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const parts = dateStr.split("-");
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return dateStr;
};

const detailAccent = {
  date: detailAccentPalette.indigo,
  startTime: detailAccentPalette.blue,
  endTime: detailAccentPalette.green,
  reason: detailAccentPalette.orange,
  rejectReason: detailAccentPalette.purple,
};

export const PermissionDetailPanel = ({
  open,
  onClose,
  permission,
  checkAccess,
  setReCallApi,
  expanded = false,
  onToggleExpand,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const goToApprovalCenter = useGoToApprovalCenter();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  // No dedicated cancel-an-approved-permission endpoint exists yet, same gap
  // as Comp Off (see CompOffDetailPanel) — this reuses the same
  // approve/reject endpoint below with status: "Cancelled" as a first
  // attempt, needs verifying against the real backend.
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  const { domain, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  // See Leave/LeaveDetailPanel.js — Approve/Reject/Cancel used to be gated
  // by plain editor permission (checkAccess). Now requires real approver
  // permission (either level counts).
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Permissions",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Permissions",
    );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: { rejected_reason: "" },
  });

  useEffect(() => {
    setRejectDialogOpen(false);
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
    setEditPanelOpen(false);
    reset({ rejected_reason: "" });
  }, [permission, reset]);

  useEffect(() => {
    register("rejected_reason", {
      required: "Reject reason is required",
      validate: {
        minLengthNoSpaces: (value) => {
          if (value?.trim().length < 3) return minimum3Characters;
          if (value?.trim().length > 255) return "Maximum length is 255 characters";
          return true;
        },
        atleastOneCharacterWithSpaceReg: (value) =>
          atleastOneCharacterWithSpace.test(value) ||
          "At least one alphabet is required",
      },
    });
  }, [register]);

  const statusFlowData = useMemo(
    () => ({
      ...(permission || {}),
      applied_by:
        permission?.applied_by ||
        permission?.employee_name ||
        permission?.username ||
        permission?.requested_by,
      applied_on:
        permission?.applied_on ||
        permission?.applied_date ||
        permission?.created_at ||
        permission?.created_date ||
        permission?.date,
      // permission?.approver_name is the assigned approver, stamped at apply
      // time before anyone has acted -- it is not proof of approval, and
      // must not stand in for approved_by (the backend already leaves
      // approved_by null/derives it properly per status; approver_name here
      // would silently resurrect the pre-stamped name for a request that
      // was cancelled straight from Pending).
      approved_by: permission?.approved_by,
      approved_at: permission?.approved_at || permission?.approved_on,
      rejected_by: permission?.rejected_by,
      rejected_at: permission?.rejected_at || permission?.rejected_on,
      cancelled_by: permission?.cancelled_by || permission?.canceled_by,
      cancelled_at:
        permission?.cancelled_at ||
        permission?.cancelled_on ||
        permission?.canceled_at ||
        permission?.canceled_on,
    }),
    [permission],
  );

  if (!permission) return null;

  const status = canonicalPermissionStatus(permission?.status);
  const isPending = status === "Pending";
  // Requested per user: allow cancelling a permission request even after it
  // has been Approved (not just while Pending), same as Comp Off.
  const canCancelApproved = status === "Approved";
  const employeeName = permission?.employee_name || "N/A";
  const employeeId = permission?.employee_id || permission?.emp_id || "-";
  const department = permission?.department_name || permission?.department || "-";
  const designation =
    permission?.designation_name || permission?.designation || "-";
  const appliedOn =
    permission?.applied_on ||
    permission?.applied_date ||
    permission?.created_at ||
    permission?.created_date ||
    permission?.requested_on ||
    "-";
  const requestReason = permission?.reason || permission?.description || "-";
  // rejected_reason is the one shared reason column the backend also writes
  // a cancellation reason into (TblPermission has no separate column for
  // it) -- the label must track actual status instead of always reading
  // "Reject Reason", matching the same convention already used by
  // OnDuty/CompOff's detail panels for this identical model shape.
  const rejectedReason = permission?.rejected_reason || "";
  const decisionReasonLabel =
    status === "Cancelled" ? "Cancel Reason" : "Reject Reason";

  const handleCloseAction = () => {
    onClose?.();
  };

  const handleEditDone = () => {
    setEditPanelOpen(false);
    setReCallApi?.(true);
    onClose?.();
  };

  const updatePermissionStatus = async (requestStatus, reason) => {
    const payload = {
      id: permission.id,
      user_id: permission.user_id,
      attendance_type: "Permission",
      reason: permission?.reason,
      status: requestStatus,
      date: toISODate(permission?.date),
      start_time: permission?.start_time,
      end_time: permission?.end_time,
      ...(requestStatus === "Rejected" && { rejected_reason: reason }),
      ...(requestStatus === "Cancelled" && { cancelled_reason: reason }),
    };
    const response = await customAPI(
      `${domain}team_zone/approve/work_adjustment/actual-special-attendance/?company_id=${requestCompanyId}`,
      "PUT",
      payload,
    );
    if (response?.ok) {
      showToast(
        requestStatus === "Rejected" ? "error" : "success",
        response?.responseDetails?.success?.[0]?.message ||
          `Permission request ${requestStatus.toLowerCase()}.`,
      );
      setReCallApi?.(true);
      setRejectDialogOpen(false);
      setCancelDialogOpen(false);
      onClose?.();
      return true;
    }
    showToast(
      "error",
      response?.message ||
        `Couldn't ${
          requestStatus === "Approved"
            ? "approve"
            : requestStatus === "Cancelled"
              ? "cancel"
              : "reject"
        } the permission request. Please try again.`,
    );
    return false;
  };

  const handleApprove = async () => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to approve this permission request",
      );
      return;
    }
    setApproving(true);
    await updatePermissionStatus("Approved");
    setApproving(false);
  };

  const submitReject = async (data) => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to reject this permission request",
      );
      return;
    }
    setRejecting(true);
    await updatePermissionStatus("Rejected", data?.rejected_reason);
    setRejecting(false);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    reset({ rejected_reason: "" });
    clearErrors("rejected_reason");
  };

  const submitCancel = async () => {
    const trimmed = cancelReason.trim();
    if (!trimmed) {
      setCancelReasonError("Reason for cancellation is required");
      return;
    }
    if (trimmed.length < 3) {
      setCancelReasonError(minimum3Characters);
      return;
    }
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to cancel this permission request",
      );
      return;
    }
    setCancelling(true);
    await updatePermissionStatus("Cancelled", trimmed);
    setCancelling(false);
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
  };

  // Reject/Approve/Cancel below used to always render, only disabled (via
  // AccessGatedAction) when hasApproverAccess was false — leaving a
  // bordered footer strip with unusable buttons for an editor-only viewer.
  // Each branch now resolves to real content or a genuine `null` (same fix
  // as OverTime/DetailPanel.js) so GlobalSidePanel's own footer border only
  // draws when there's actually something to show.
  const footerContent = isPending ? (
    hasApproverAccess ? (
      <Mui.Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 1.5,
        }}
      >
        <Mui.Button
          variant="outlined"
          startIcon={<HighlightOffIcon />}
          onClick={() => setRejectDialogOpen(true)}
          sx={{
            ...designShell.panelDangerButton,
            color: "#B42318",
            borderColor: "#F5C2C7",
            backgroundColor: "#FEF3F2",
            "&:hover": {
              borderColor: "#E59CA5",
              backgroundColor: "#FEE4E2",
            },
          }}
        >
          Reject
        </Mui.Button>
        <PrimaryButton
          startIcon={<CheckCircleOutlineIcon />}
          loading={approving}
          onClick={handleApprove}
        >
          Approve
        </PrimaryButton>
      </Mui.Box>
    ) : null
  ) : canCancelApproved ? (
    hasApproverAccess ? (
      <Mui.Stack spacing={1.5}>
        <Mui.Typography
          sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}
        >
          This request has already been approved.
        </Mui.Typography>
        <RequestCancelButton onClick={() => setCancelDialogOpen(true)}>
          Cancel Permission
        </RequestCancelButton>
      </Mui.Stack>
    ) : null
  ) : (
    <Mui.Typography
      sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}
    >
      This request has already been {status?.toLowerCase() || "processed"}.
    </Mui.Typography>
  );

  const rejectDialog = (
    <GlobalConfirmDialog
      open={rejectDialogOpen}
      title="Reject Permission Request"
      onCancel={closeRejectDialog}
      onConfirm={() => {
        if (!watch("rejected_reason")?.trim()) {
          setError("rejected_reason", { type: "required", message: "Reject reason is required" });
          return;
        }
        handleSubmit(submitReject)();
      }}
      confirmLabel="Reject"
      cancelLabel="Cancel"
      loading={rejecting}
      danger
      reasonValue={watch("rejected_reason") || ""}
      onReasonChange={(value) => setValue("rejected_reason", value, { shouldValidate: true })}
      reasonError={errors?.rejected_reason?.message || ""}
      reasonPlaceholder="Provide a reason for rejection"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        Enter the reason for rejecting this permission request.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  const cancelDialog = (
    <GlobalConfirmDialog
      open={cancelDialogOpen}
      title="Cancel Permission Request"
      onCancel={closeCancelDialog}
      onConfirm={submitCancel}
      confirmLabel="Continue"
      cancelLabel="Keep"
      loading={cancelling}
      loadingLabel="Cancelling..."
      danger
      reasonValue={cancelReason}
      onReasonChange={(value) => {
        setCancelReason(value);
        if (cancelReasonError) setCancelReasonError("");
      }}
      reasonError={cancelReasonError}
      reasonPlaceholder="Provide a reason for cancelling this approved permission request"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        This permission request has already been approved. Enter a reason to
        cancel it.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={handleCloseAction}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        title="Permission Request"
        subtitle="Request details and status"
        headerActions={
          isPending && checkAccess ? (
            <Mui.Tooltip title="Edit permission request" arrow>
              <Mui.IconButton
                aria-label="Edit permission request"
                size="small"
                onClick={() => setEditPanelOpen(true)}
                sx={{
                  ...designShell.panelSoftIconButton,
                  color: designColors.slate500,
                }}
              >
                <EditIcon fontSize="small" />
              </Mui.IconButton>
            </Mui.Tooltip>
          ) : null
        }
        headerSx={{ px: 2.5, pt: 2.5, pb: 1.5 }}
        PaperProps={{
          sx: {
            backgroundColor: designColors.white,
            maxWidth: "92vw",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            overflow: "hidden",
          },
        }}
        breadcrumb={[
          { label: "Approval Center", onClick: goToApprovalCenter },
          { label: "Permission", onClick: handleCloseAction },
          employeeName || "Permission Request",
        ]}
        footer={footerContent}
      >
        <Mui.Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <RequestDetailHeader
            userDetails={{
              image_url: permission?.image_url,
              employee_id: employeeId,
              user_name: employeeName,
              department_name: department,
              designation,
            }}
            status={status}
            colorCode={permission?.color_code || permissionStatusVisual(status).accent}
          />

          <Mui.Box sx={detailCardSx}>
            <Mui.Typography sx={{ ...detailSectionLabelSx, mb: 1.75 }}>
              Request Details
            </Mui.Typography>
            <RequestDetailField
              icon={CalendarMonthIcon}
              label="Date"
              value={permission?.date || "-"}
              accent={detailAccent.date}
            />
            <RequestDetailField
              icon={CalendarMonthIcon}
              label="Applied On"
              value={appliedOn}
              accent={detailAccent.date}
            />
            <RequestDetailField
              icon={AccessTimeIcon}
              label="Start Time"
              value={permission?.start_time || "-"}
              accent={detailAccent.startTime}
            />
            <RequestDetailField
              icon={AccessTimeIcon}
              label="End Time"
              value={permission?.end_time || "-"}
              accent={detailAccent.endTime}
            />
            <RequestDetailField
              icon={DescriptionOutlinedIcon}
              label="Reason"
              value={requestReason}
              accent={detailAccent.reason}
              multiline
              isLast={!rejectedReason}
            />
            {rejectedReason ? (
              <RequestDetailField
                icon={DescriptionOutlinedIcon}
                label={decisionReasonLabel}
                value={rejectedReason}
                accent={detailAccent.rejectReason}
                multiline
                isLast
              />
            ) : null}
          </Mui.Box>

          <RequestStatusTimeline
            endDate={permission?.date}
            data={statusFlowData}
            variant="stepper"
            hidePendingStep
          />
          <ApprovalEditAuditTrail data={permission} />
        </Mui.Box>
      </GlobalSidePanel>
      {editPanelOpen ? (
        <PermissionApplyPanel
          open={editPanelOpen}
          onClose={() => setEditPanelOpen(false)}
          setReCallApi={handleEditDone}
          editRecord={permission}
        />
      ) : null}
      {rejectDialog}
      {cancelDialog}
    </>
  );
};
